import ko from 'knockout'
import moment from 'moment';
import {formatDecimal} from '../../decimal-utils';
import {CustomError, customFetch as fetch} from '../../fetch';
import {closePopups} from '../../init';
import debounce from 'lodash.debounce';
import { errorToast } from '../../toastr-helper';


interface AddEmployeeVacationRequestOptions {
    refresh(model: AddEmployeeVacationRequestModel): void;

    postLocation: string;
    getVacationHoursLocation?: string;
    employeeId?: string;
}

class AddEmployeeVacationRequestModel {
    error: KnockoutObservable<string>;
    saving: KnockoutObservable<boolean>;

    minDate: KnockoutObservable<moment.Moment>;
    from: KnockoutObservable<moment.Moment>;
    to: KnockoutObservable<moment.Moment>;
    vacationHours: KnockoutObservable<number>;

    overTwoYears: KnockoutObservable<boolean>;
    fromOtherYear: KnockoutObservable<moment.Moment>;
    toOtherYear: KnockoutObservable<moment.Moment>;
    vacationHoursOtherYear: KnockoutObservable<number>;
    isSpecialLeave: KnockoutObservable<boolean>;
    reason: KnockoutObservable<string>;

    validationErrors: KnockoutObservableArray<CustomError>;

    options: AddEmployeeVacationRequestOptions;

    constructor(options: { getVacationHoursLocation?: string; refresh: () => void; postLocation: string }) {
        this.validationErrors = ko.observableArray<CustomError>();
        this.saving = ko.observable<boolean>();
        this.minDate = ko.observable<moment.Moment>(moment().startOf('d'));
        this.isSpecialLeave = ko.observable<boolean>(false);

        this.from = ko.observable<moment.Moment>()
            .extend({
                errorHandler: {source: this.validationErrors, id: 'From'}
            });
        this.to = ko.observable<moment.Moment>()
            .extend({
                errorHandler: {source: this.validationErrors, id: 'To'}
            });
        this.from.subscribe(async newValue => {
            this.vacationHours(await this.calculateVacationHours(newValue, this.to()));
        });
        this.to.subscribe(async newValue => {
            this.vacationHours(await this.calculateVacationHours(this.from(), newValue));
        });

        this.vacationHours = ko.observable<number>(0)
            .extend({
                errorHandler: {source: this.validationErrors, id: 'VacationHours'}
            });
        this.overTwoYears = ko.observable<boolean>(false);
        this.fromOtherYear = ko.observable<moment.Moment>()
            .extend({
                errorHandler: {source: this.validationErrors, id: 'FromOtherYear'}
            });
        this.toOtherYear = ko.observable<moment.Moment>()
            .extend({
                errorHandler: {source: this.validationErrors, id: 'ToOtherYear'}
            });
        this.fromOtherYear.subscribe(async newValue => {
            this.vacationHoursOtherYear(await this.calculateVacationHours(this.toOtherYear(), newValue));
        });
        this.toOtherYear.subscribe(async newValue => {
            this.vacationHoursOtherYear(await this.calculateVacationHours(this.fromOtherYear(), newValue));
        });

        this.vacationHoursOtherYear = ko.observable<number>(0)
            .extend({
                errorHandler: {source: this.validationErrors, id: 'VacationHoursOtherYear'}
            });
        this.error = ko.observable<string>()
            .extend({
                errorHandler: {source: this.validationErrors, id: 'Error'}
            });

        this.reason = ko.observable<string>()
            .extend({
                errorHandler: {source: this.validationErrors, id: 'Reason'}
            });

        this.to.subscribe(() => this.splitYears());
        this.from.subscribe((newValue: moment.Moment) => {
            this.splitYears();

            if (newValue) {
                this.to(newValue);
            }
        });

        this.options = options;
    }

    private splitYears() {
        const from = this.from();
        const to = this.to();
        if (from && to && from.year() !== to.year()) {
            this.overTwoYears(true);
            const firstDayNextYear = moment({
                year: to.year(),
                day: 1,
                month: 0
            });
            this.to(moment(firstDayNextYear).add(-1, 'minute'));
            this.fromOtherYear(firstDayNextYear);
            this.toOtherYear(to);
            this.vacationHoursOtherYear(Math.max(this.vacationHours() - Math.ceil(this.vacationHours() / 2), 1));
            this.vacationHours(Math.max(this.vacationHours() - this.vacationHoursOtherYear(), 1));
        }
    }

    async calculateVacationHours(from: moment.Moment, to: moment.Moment): Promise<number> {
        
        if(this.options.getVacationHoursLocation == null) {
            return 0;
        }
        
        let fd = new FormData();

        if (!from || !to) {
            return 0;
        }

        if (this.options.employeeId) {
            fd.append('id', this.options.employeeId);
        }

        fd.append('from', from.format('YYYY-MM-DD HH:mm'));
        fd.append('to', to.format('YYYY-MM-DD HH:mm'));

        const response = await fetch(this.options.getVacationHoursLocation, {
            method: 'POST',
            body: fd
        });

        this.saving(false);

        if (response.ok) {
            return response.data;
        }

        return 0;
    }

    resetTwoYears() {
        this.overTwoYears(false);
        this.fromOtherYear(null);
        this.toOtherYear(null);
        this.vacationHours(this.vacationHours() + this.vacationHoursOtherYear());
        this.vacationHoursOtherYear(null);
    }

    debouncedSubmit = debounce(async () => this.submit(), 250);

    async submit() {
        const fd = new FormData();

        if (this.from()) {
            fd.append('from', this.from().format('YYYY-MM-DD HH:mm'));
        }

        if (this.to()) {
            fd.append('to', this.to().format('YYYY-MM-DD HH:mm'));
        }

        fd.append('vacationHours', formatDecimal(this.vacationHours()));
        fd.append('isSpecialLeave', this.isSpecialLeave().toString());
        if (this.overTwoYears()) {
            fd.append('fromOtherYear', this.fromOtherYear() ? this.fromOtherYear().format('YYYY-MM-DD HH:mm') : '');
            fd.append('toOtherYear', this.toOtherYear() ? this.toOtherYear().format('YYYY-MM-DD HH:mm') : '');
            fd.append('vacationHoursOtherYear', formatDecimal(this.vacationHoursOtherYear()));
        }
        if (this.options.employeeId) {
            fd.append('employeeId', this.options.employeeId);
        }

        fd.append('reason', this.reason() || '');

        this.saving(true);

        const response = await fetch(this.options.postLocation, {
            method: 'POST',
            body: fd
        });

        this.saving(false);

        if (response.ok) {
            this.options.refresh(this);
            this.reset();
            this.closePopup();
        } else {
            if (response.errors && response.errors.length > 0) {
                this.validationErrors(response.errors);
            }
            else if (response.data.length > 0) {
                errorToast(response.data);
            }
        }
    }

    reset() {
        this.from(null);
        this.to(null);
        this.vacationHours(null);
        this.overTwoYears(false);
        this.fromOtherYear(null);
        this.toOtherYear(null);
        this.vacationHoursOtherYear(null);
        this.validationErrors([]);
    }

    closePopup() {
        closePopups();
    }
}

export default AddEmployeeVacationRequestModel;
